package com.example.hukakmp.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.DrawerValue
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalNavigationDrawer
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.rememberDrawerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.layout.boundsInWindow
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.example.hukakmp.model.HukaUser
import com.example.hukakmp.viewmodel.ReportesViewModel
import kotlinx.coroutines.launch

private val HomeHeaderBlue = Color(0xFF11A8EA)
private val HomeHeaderBlueLight = Color(0xFF22C7F3)
private val HomeBackground = Color(0xFF031B29)
private val HomeCard = Color(0xFF082E49)
private val HomeCardEdge = Color(0xFF0877BE)
private val HomeIconBlue = Color(0xFF0A9EE8)
private val HomeIconCyan = Color(0xFF12C9D7)
private val HomeText = Color(0xFFF7FBFF)
private val HomeSubText = Color(0xFFC6DCEC)

private enum class HomeIcon {
    CHAT,
    WAVES,
    INFO,
}

@Composable
fun HomeScreen(
    user: HukaUser,
    reportesState: ReportesViewModel.ReportesUiState,
    onNuevoParte: () -> Unit,
    onReportes: () -> Unit,
    onPescadex: () -> Unit,
    onIdentificar: () -> Unit,
    onChat: () -> Unit,
    onContador: () -> Unit,
    onTorneos: () -> Unit,
    onLogros: () -> Unit,
    onSignOut: () -> Unit,
) {
    val drawerState = rememberDrawerState(initialValue = DrawerValue.Closed)
    val scope = rememberCoroutineScope()
    val tutorialStore = rememberMainTutorialStore()
    val openUrl = rememberExternalUrlOpener()

    var tutorialStep by remember { mutableIntStateOf(if (tutorialStore.hasSeen) -1 else 0) }
    var showExtraInfo by remember { mutableStateOf(false) }
    var menuButtonBounds by remember { mutableStateOf<Rect?>(null) }
    var consultCardBounds by remember { mutableStateOf<Rect?>(null) }

    fun finishTutorial() {
        tutorialStore.markSeen()
        tutorialStep = -1
        scope.launch { drawerState.close() }
    }

    fun nextTutorial() {
        if (tutorialStep >= 5) finishTutorial() else tutorialStep += 1
    }

    fun replayTutorial() {
        tutorialStore.reset()
        tutorialStep = 0
        scope.launch { drawerState.close() }
    }

    fun closeAnd(action: () -> Unit) {
        scope.launch { drawerState.close() }
        action()
    }

    LaunchedEffect(tutorialStep) {
        when (tutorialStep) {
            in 2..5 -> drawerState.open()
            0, 1 -> drawerState.close()
        }
    }

    val firstName = user.displayName
        ?.trim()
        ?.substringBefore(" ")
        ?.takeIf { it.isNotBlank() }
        ?: "Pescador"

    ModalNavigationDrawer(
        drawerState = drawerState,
        drawerContent = {
            HukaDrawerContent(
                user = user,
                selected = "inicio",
                onInicio = { scope.launch { drawerState.close() } },
                onNuevoParte = { closeAnd(onNuevoParte) },
                onReportes = { closeAnd(onReportes) },
                onPescadex = { closeAnd(onPescadex) },
                onIdentificar = { closeAnd(onIdentificar) },
                onChat = { closeAnd(onChat) },
                onContador = { closeAnd(onContador) },
                onTorneos = { closeAnd(onTorneos) },
                onLogros = { closeAnd(onLogros) },
                onSignOut = onSignOut,
                onCloseDrawer = { scope.launch { drawerState.close() } },
                tutorialStep = tutorialStep,
                onTutorialNext = ::nextTutorial,
                onTutorialSkip = ::finishTutorial,
                onReplayTutorial = ::replayTutorial,
            )
        },
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(HomeBackground),
        ) {
            Column(modifier = Modifier.fillMaxSize()) {
                HomeHeader(
                    firstName = firstName,
                    onMenu = { scope.launch { drawerState.open() } },
                    onMenuPositioned = { menuButtonBounds = it },
                )

                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .verticalScroll(rememberScrollState()),
                    contentAlignment = Alignment.TopCenter,
                ) {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .widthIn(max = 760.dp)
                            .padding(horizontal = 18.dp, vertical = 22.dp),
                        verticalArrangement = Arrangement.spacedBy(14.dp),
                    ) {
                        HukaMenuOptionCard(
                            modifier = Modifier.onGloballyPositioned {
                                consultCardBounds = it.boundsInWindow()
                            },
                            icon = HomeIcon.CHAT,
                            title = "Hacer una consulta",
                            subtitle = "Preguntale lo que quieras a Huka",
                            accent = HomeIconBlue,
                            onClick = onChat,
                        )

                        HukaMenuOptionCard(
                            icon = HomeIcon.WAVES,
                            title = "Información de mareas",
                            subtitle = "Abrí la tabla oficial",
                            accent = Color(0xFF10B9EE),
                            onClick = {
                                openUrl("https://www.hidro.gov.ar/oceanografia/tmareas/form_tmareas.asp")
                            },
                        )

                        HukaMenuOptionCard(
                            icon = HomeIcon.INFO,
                            title = "Info adicional",
                            subtitle = "Especies, vedas, permisos y más",
                            accent = HomeIconCyan,
                            expanded = showExtraInfo,
                            onClick = { showExtraInfo = !showExtraInfo },
                        )

                        if (showExtraInfo) {
                            Surface(
                                modifier = Modifier.fillMaxWidth(),
                                shape = RoundedCornerShape(20.dp),
                                color = Color(0xFF06263A),
                                border = BorderStroke(1.dp, HomeCardEdge.copy(alpha = 0.45f)),
                            ) {
                                Column(modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp)) {
                                    InfoLinkRow("Guía de especies") {
                                        openUrl("https://www.pescaargentina.com.ar/contenidos/especies-argentinas")
                                    }
                                    InfoLinkRow("Viento") {
                                        openUrl("https://www.windguru.cz/53")
                                    }
                                    InfoLinkRow("Vedas vigentes") {
                                        openUrl("https://www.argentina.gob.ar/inidep/areas-de-veda")
                                    }
                                    InfoLinkRow("Permisos y reglamentos") {
                                        openUrl("https://www.pescaargentina.com.ar/reglamento-licencia-pesca-deportiva")
                                    }
                                    InfoLinkRow("Calendario lunar") {
                                        openUrl("https://www.pescaargentina.com.ar/fases-lunares/")
                                    }
                                }
                            }
                        }

                        Spacer(Modifier.height(4.dp))

                        QuickActivityCard(
                            reportesState = reportesState,
                            onNuevoParte = onNuevoParte,
                            onReportes = onReportes,
                        )

                        Spacer(Modifier.height(18.dp))
                    }
                }
            }

            val tutorialTarget = when (tutorialStep) {
                0 -> menuButtonBounds
                1 -> consultCardBounds
                else -> null
            }

            tutorialTarget?.let { target ->
                val (title, description) = when (tutorialStep) {
                    0 -> "Explorá Huka" to
                        "Desde el menú accedés a Crear parte, Pescadex, Identificar pez, Logros, Torneos y más."
                    else -> "Consultá a Huka" to
                        "Usá el chat para hacer consultas de pesca de forma rápida y conversacional."
                }

                CoachMarkOverlay(
                    target = target,
                    title = title,
                    description = description,
                    stepLabel = "${tutorialStep + 1} de 6",
                    onNext = ::nextTutorial,
                    onSkip = ::finishTutorial,
                )
            }
        }
    }
}

@Composable
private fun HomeHeader(
    firstName: String,
    onMenu: () -> Unit,
    onMenuPositioned: (Rect) -> Unit,
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .background(
                Brush.horizontalGradient(
                    listOf(HomeHeaderBlue, HomeHeaderBlueLight),
                ),
            ),
    ) {
        Canvas(modifier = Modifier.matchParentSize()) {
            val wave = Path().apply {
                moveTo(size.width * 0.43f, size.height * 0.72f)
                cubicTo(
                    size.width * 0.60f, size.height * 0.45f,
                    size.width * 0.72f, size.height * 0.90f,
                    size.width, size.height * 0.57f,
                )
            }
            drawPath(
                wave,
                color = Color.White.copy(alpha = 0.10f),
                style = Stroke(width = 10f, cap = StrokeCap.Round),
            )
        }

        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 18.dp, vertical = 18.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            HukaLogoMark(
                modifier = Modifier.size(64.dp),
                tint = Color.White,
            )
            Spacer(Modifier.width(16.dp))
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    "Chat Huka",
                    color = Color.White,
                    fontSize = 25.sp,
                    fontWeight = FontWeight.ExtraBold,
                )
                Text(
                    "Hola $firstName, elegí qué necesitás",
                    color = Color.White.copy(alpha = 0.90f),
                    fontSize = 14.sp,
                    modifier = Modifier.padding(top = 2.dp),
                )
            }
            Box(
                modifier = Modifier
                    .size(46.dp)
                    .onGloballyPositioned { onMenuPositioned(it.boundsInWindow()) }
                    .clickable(onClick = onMenu),
                contentAlignment = Alignment.Center,
            ) {
                MenuGlyph()
            }
        }
    }
}

@Composable
private fun MenuGlyph() {
    Canvas(modifier = Modifier.size(26.dp)) {
        val stroke = 2.6.dp.toPx()
        val left = size.width * 0.16f
        val right = size.width * 0.84f
        listOf(0.28f, 0.50f, 0.72f).forEach { y ->
            drawLine(
                color = Color.White,
                start = Offset(left, size.height * y),
                end = Offset(right, size.height * y),
                strokeWidth = stroke,
                cap = StrokeCap.Round,
            )
        }
    }
}

@Composable
private fun HukaMenuOptionCard(
    icon: HomeIcon,
    title: String,
    subtitle: String,
    accent: Color,
    modifier: Modifier = Modifier,
    expanded: Boolean = false,
    onClick: () -> Unit,
) {
    Surface(
        modifier = modifier
            .fillMaxWidth()
            .clickable(onClick = onClick),
        shape = RoundedCornerShape(24.dp),
        color = HomeCard,
        border = BorderStroke(1.5.dp, HomeCardEdge.copy(alpha = 0.88f)),
        shadowElevation = 4.dp,
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 20.dp, vertical = 20.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .size(66.dp)
                    .background(
                        Brush.linearGradient(
                            listOf(accent.copy(alpha = 0.96f), accent.copy(alpha = 0.62f)),
                        ),
                        RoundedCornerShape(18.dp),
                    ),
                contentAlignment = Alignment.Center,
            ) {
                HomeCardIcon(icon)
            }

            Spacer(Modifier.width(18.dp))

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    title,
                    color = HomeText,
                    fontSize = 20.sp,
                    fontWeight = FontWeight.Bold,
                )
                Text(
                    subtitle,
                    color = HomeSubText,
                    fontSize = 14.sp,
                    modifier = Modifier.padding(top = 5.dp),
                )
            }

            Chevron(expanded = expanded)
        }
    }
}

@Composable
private fun HomeCardIcon(icon: HomeIcon) {
    Canvas(modifier = Modifier.size(34.dp)) {
        val c = Color.White
        val stroke = 2.8.dp.toPx()

        when (icon) {
            HomeIcon.CHAT -> {
                val bubble = Path().apply {
                    moveTo(size.width * 0.18f, size.height * 0.18f)
                    lineTo(size.width * 0.82f, size.height * 0.18f)
                    lineTo(size.width * 0.82f, size.height * 0.65f)
                    lineTo(size.width * 0.48f, size.height * 0.65f)
                    lineTo(size.width * 0.30f, size.height * 0.82f)
                    lineTo(size.width * 0.31f, size.height * 0.65f)
                    lineTo(size.width * 0.18f, size.height * 0.65f)
                    close()
                }
                drawPath(bubble, c, style = Stroke(width = stroke, join = androidx.compose.ui.graphics.StrokeJoin.Round))
                listOf(0.39f, 0.50f, 0.61f).forEach { x ->
                    drawCircle(c, radius = size.minDimension * 0.035f, center = Offset(size.width * x, size.height * 0.43f))
                }
            }

            HomeIcon.WAVES -> {
                listOf(0.32f, 0.50f, 0.68f).forEach { y ->
                    val p = Path().apply {
                        moveTo(size.width * 0.16f, size.height * y)
                        cubicTo(
                            size.width * 0.32f, size.height * (y - 0.10f),
                            size.width * 0.42f, size.height * (y + 0.10f),
                            size.width * 0.55f, size.height * y,
                        )
                        cubicTo(
                            size.width * 0.68f, size.height * (y - 0.10f),
                            size.width * 0.76f, size.height * (y + 0.10f),
                            size.width * 0.86f, size.height * y,
                        )
                    }
                    drawPath(p, c, style = Stroke(width = stroke, cap = StrokeCap.Round))
                }
            }

            HomeIcon.INFO -> {
                drawCircle(
                    color = c,
                    radius = size.minDimension * 0.38f,
                    center = center,
                    style = Stroke(width = stroke),
                )
                drawCircle(c, radius = size.minDimension * 0.04f, center = Offset(center.x, size.height * 0.31f))
                drawLine(
                    c,
                    Offset(center.x, size.height * 0.44f),
                    Offset(center.x, size.height * 0.69f),
                    strokeWidth = stroke,
                    cap = StrokeCap.Round,
                )
            }
        }
    }
}

@Composable
private fun Chevron(expanded: Boolean) {
    Canvas(modifier = Modifier.size(28.dp)) {
        val c = Color(0xFF20D6FF)
        val stroke = 3.dp.toPx()
        if (expanded) {
            drawLine(c, Offset(size.width * 0.22f, size.height * 0.62f), Offset(size.width * 0.50f, size.height * 0.36f), stroke, StrokeCap.Round)
            drawLine(c, Offset(size.width * 0.50f, size.height * 0.36f), Offset(size.width * 0.78f, size.height * 0.62f), stroke, StrokeCap.Round)
        } else {
            drawLine(c, Offset(size.width * 0.36f, size.height * 0.22f), Offset(size.width * 0.64f, size.height * 0.50f), stroke, StrokeCap.Round)
            drawLine(c, Offset(size.width * 0.64f, size.height * 0.50f), Offset(size.width * 0.36f, size.height * 0.78f), stroke, StrokeCap.Round)
        }
    }
}

@Composable
private fun InfoLinkRow(text: String, onClick: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(vertical = 11.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Canvas(modifier = Modifier.size(12.dp)) {
            drawCircle(HomeHeaderBlue, radius = size.minDimension * 0.28f)
        }
        Spacer(Modifier.width(10.dp))
        Text(
            text,
            fontSize = 14.sp,
            color = HomeSubText,
            fontWeight = FontWeight.Medium,
        )
    }
}

@Composable
private fun QuickActivityCard(
    reportesState: ReportesViewModel.ReportesUiState,
    onNuevoParte: () -> Unit,
    onReportes: () -> Unit,
) {
    val reportes = reportesState.reportes
    val captures = reportes.sumOf { it.cantidadTotal }

    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(24.dp),
        color = Color(0xFF06263A),
        border = BorderStroke(1.dp, HomeCardEdge.copy(alpha = 0.55f)),
    ) {
        Column(modifier = Modifier.padding(18.dp)) {
            Text(
                "Tu actividad",
                color = HomeText,
                fontSize = 18.sp,
                fontWeight = FontWeight.Bold,
            )
            Text(
                if (reportesState.isLoading) {
                    "Sincronizando tus datos…"
                } else {
                    "${reportes.size} jornadas · $captures capturas registradas"
                },
                color = HomeSubText,
                fontSize = 13.sp,
                modifier = Modifier.padding(top = 4.dp),
            )

            Row(
                modifier = Modifier.fillMaxWidth().padding(top = 14.dp),
                horizontalArrangement = Arrangement.spacedBy(10.dp),
            ) {
                Surface(
                    modifier = Modifier.weight(1f).clickable(onClick = onNuevoParte),
                    shape = RoundedCornerShape(15.dp),
                    color = HomeHeaderBlue,
                ) {
                    Text(
                        "+ Crear parte",
                        modifier = Modifier.padding(13.dp),
                        color = Color.White,
                        fontWeight = FontWeight.Bold,
                    )
                }
                Surface(
                    modifier = Modifier.weight(1f).clickable(onClick = onReportes),
                    shape = RoundedCornerShape(15.dp),
                    color = Color(0xFF0A466B),
                    border = BorderStroke(1.dp, HomeCardEdge),
                ) {
                    Text(
                        "Ver reportes",
                        modifier = Modifier.padding(13.dp),
                        color = HomeText,
                        fontWeight = FontWeight.Bold,
                    )
                }
            }
        }
    }
}
